import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { PageHeader, fmt } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  CreditCard,
  Smartphone,
  Banknote,
  CheckCircle2,
  History,
  ReceiptText,
  ShoppingCart,
} from "lucide-react";
import { toast } from "sonner";
import { hasRole, useAuth } from "@/lib/auth-store";
import {
  tabStore,
  useCurrentTab,
  useTabStore,
  type TabPayment,
} from "@/lib/tab-store";
import { useRealtimeTabs, markLocalAction } from "@/hooks/useRealtimeTabs";

export const Route = createFileRoute("/tab")({
  beforeLoad: ({ context }) => {
    if (!hasRole(context.user, "viewer")) {
      throw redirect({ to: "/" });
    }
  },
  head: () => ({
    meta: [
      { title: "My Tab" },
      { name: "description", content: "View and manage your running tab." },
    ],
  }),
  component: TabPage,
});

const paymentMethods: {
  value: TabPayment["method"];
  label: string;
  icon: typeof Banknote;
}[] = [
  { value: "cash", label: "Cash", icon: Banknote },
  { value: "mpesa", label: "M-Pesa", icon: Smartphone },
  { value: "bank", label: "Bank", icon: CreditCard },
];

function TabPage() {
  const { user } = useAuth();
  const currentTab = useCurrentTab();

  // Listen for real-time updates (e.g., waiter adds items, cashier issues)
  useRealtimeTabs();

  const allTabs = useTabStore((s) =>
    s.tabs.filter((t) => t.customerId === user?.id),
  );
  const [payAmount, setPayAmount] = useState("");
  const [payMethod, setPayMethod] = useState<TabPayment["method"]>("cash");
  const [payDialogOpen, setPayDialogOpen] = useState(false);
  const navigate = useNavigate();

  const tabTotal = currentTab
    ? currentTab.items.reduce((a, i) => a + i.total, 0)
    : 0;
  const paidTotal = currentTab
    ? currentTab.payments.reduce((a, p) => a + p.amount, 0)
    : 0;
  const balance = tabTotal - paidTotal;
  const closedTabs = allTabs.filter((t) => t.status === "closed");

  const handlePay = async () => {
    if (!payAmount || +payAmount <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    markLocalAction();
    const amount = +payAmount;
    const updatedTab = await tabStore.payTab(amount, payMethod);
    if (updatedTab) {
      toast.success(`Payment of ${fmt(amount)} recorded`);
      setPayAmount("");
      setPayDialogOpen(false);
    }
  };

  const handleCloseTab = async () => {
    if (balance > 0) {
      toast.error("Clear the balance before closing");
      return;
    }
    markLocalAction();
    await tabStore.closeTab();
    toast.success("Tab closed. Thank you!");
  };

  return (
    <div>
      <PageHeader title="My Tab" subtitle={user?.displayName} />

      {!currentTab ? (
        <div className="flex flex-col items-center justify-center px-4 pt-20 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-accent">
            <ReceiptText className="h-8 w-8 text-muted-foreground" />
          </div>
          <h2 className="mt-4 font-display text-lg font-semibold">
            No Open Tab
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Order drinks from the menu to start your tab
          </p>
          <Button className="mt-6" onClick={() => navigate({ to: "/sell" })}>
            <ShoppingCart className="h-4 w-4" /> Order Drinks
          </Button>
        </div>
      ) : (
        <>
          {/* Tab summary card */}
          <div className="mx-4 mt-4 overflow-hidden rounded-2xl bg-gradient-to-br from-primary to-primary/70 p-5 text-primary-foreground shadow-lg">
            <p className="text-xs uppercase tracking-widest opacity-80">
              Running Tab
            </p>
            <p className="mt-1 font-display text-4xl font-bold">
              {fmt(balance)}
            </p>
            <div className="mt-3 flex items-center gap-1 text-sm">
              <ReceiptText className="h-4 w-4" />
              <span>Total: {fmt(tabTotal)}</span>
              <span className="opacity-60">· Paid: {fmt(paidTotal)}</span>
            </div>
          </div>

          {/* Items */}
          <section className="px-4 pt-6">
            <h2 className="font-display text-base font-semibold mb-2">
              Items Ordered
            </h2>
            {currentTab.items.length === 0 ? (
              <p className="text-sm text-muted-foreground">No items yet</p>
            ) : (
              <div className="divide-y divide-border rounded-xl border border-border bg-card">
                {currentTab.items.map((item, idx) => (
                  <div
                    key={item.itemId + idx}
                    className="flex items-center gap-3 px-4 py-3"
                  >
                    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent text-accent-foreground font-display text-sm font-bold">
                      {item.name.slice(0, 2).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="truncate text-sm font-medium">
                        {item.name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {item.qty} × {fmt(item.unitPrice)}
                      </p>
                    </div>
                    <p className="font-display text-sm font-semibold">
                      {fmt(item.total)}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Payments */}
          {currentTab.payments.length > 0 && (
            <section className="px-4 pt-6">
              <h2 className="font-display text-base font-semibold mb-2">
                Payment History
              </h2>
              <div className="divide-y divide-border rounded-xl border border-border bg-card">
                {currentTab.payments.map((p) => (
                  <div key={p.id} className="flex items-center gap-3 px-4 py-3">
                    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-secondary">
                      {p.method === "cash" ? (
                        <Banknote className="h-4 w-4" />
                      ) : p.method === "mpesa" ? (
                        <Smartphone className="h-4 w-4" />
                      ) : (
                        <CreditCard className="h-4 w-4" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium capitalize">
                        {p.method}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(p.at).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </p>
                    </div>
                    <p className="font-display text-sm font-semibold text-primary">
                      {fmt(p.amount)}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Actions */}
          <section className="px-4 pt-6 pb-24">
            <div className="grid grid-cols-2 gap-3">
              <Dialog open={payDialogOpen} onOpenChange={setPayDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    variant="outline"
                    className="h-auto py-4 min-h-[64px]"
                    disabled={balance <= 0}
                  >
                    <Banknote className="h-5 w-5" />
                    <span className="mt-1 block text-sm font-medium">
                      Make Payment
                    </span>
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Make a Payment</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label>Amount</Label>
                      <Input
                        type="number"
                        value={payAmount}
                        onChange={(e) => setPayAmount(e.target.value)}
                        placeholder="e.g. 500"
                        autoFocus
                      />
                    </div>
                    <div>
                      <Label>Payment Method</Label>
                      <div className="mt-1 grid grid-cols-3 gap-2">
                        {paymentMethods.map((pm) => {
                          const Icon = pm.icon;
                          return (
                            <button
                              key={pm.value}
                              onClick={() => setPayMethod(pm.value)}
                              className={`flex flex-col items-center gap-1 rounded-lg border p-3 text-sm font-medium capitalize transition ${
                                payMethod === pm.value
                                  ? "border-primary bg-accent text-accent-foreground"
                                  : "border-border bg-card text-muted-foreground"
                              }`}
                            >
                              <Icon className="h-5 w-5" />
                              {pm.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <p className="text-center text-sm text-muted-foreground">
                      Balance: {fmt(balance)}
                    </p>
                  </div>
                  <DialogFooter>
                    <Button onClick={handlePay}>Record Payment</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              <Button
                variant={balance <= 0 ? "default" : "secondary"}
                className="h-auto py-4 min-h-[64px]"
                disabled={balance > 0}
                onClick={handleCloseTab}
              >
                <CheckCircle2 className="h-5 w-5" />
                <span className="mt-1 block text-sm font-medium">
                  {balance <= 0 ? "Close Tab" : `Pay ${fmt(balance)} first`}
                </span>
              </Button>
            </div>
          </section>
        </>
      )}

      {/* Closed tabs history */}
      {closedTabs.length > 0 && (
        <section className="px-4 pt-2 pb-24">
          <div className="flex items-center gap-2 mb-3">
            <History className="h-4 w-4 text-muted-foreground" />
            <h2 className="font-display text-base font-semibold">
              Payment History
            </h2>
          </div>
          <div className="space-y-2">
            {closedTabs.map((tab) => {
              const tabPaid = tab.payments.reduce((a, p) => a + p.amount, 0);
              return (
                <div
                  key={tab.id}
                  className="rounded-xl border border-border bg-card p-3"
                >
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium">Tab Closed</p>
                    <span className="rounded-full bg-accent px-2 py-0.5 text-[10px] font-semibold uppercase text-muted-foreground">
                      {tab.items.length} items
                    </span>
                  </div>
                  <div className="mt-1 flex items-center justify-between text-xs text-muted-foreground">
                    <span>
                      {new Date(tab.createdAt).toLocaleDateString("en-KE", {
                        day: "numeric",
                        month: "short",
                      })}
                    </span>
                    <span>Paid: {fmt(tabPaid)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}
